import { Parser } from 'expr-eval';
import { RatingsAndReviewMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewMetricTypes';
import { FieldDefinition, METRICTYPE } from 'src/utils/entityDefinitions/entityDefinitionTypes';

const expressionParser = new Parser();

export const BEACONPRO_REVIEW_FIELDS: {
  [key: string]: FieldDefinition;
} = {
  reviewQualityScore: {
    name: 'reviewQualityScore',
    displayName: 'Review Quality Score',
    metricType: METRICTYPE.PERCENT,
    aggregationFunction: 'avg',
    canBeExported: true
  },
  wordCountQualityScore: {
    name: 'wordCountQualityScore',
    displayName: 'Word Count Quality Score',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunction: 'avg',
    canBeExported: true
  },
  keywordCountQualityScore: {
    name: 'keywordCountQualityScore',
    displayName: 'Keyword Count Quality Score',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunction: 'avg',
    canBeExported: true
  },
  writingQualityScore: {
    name: 'writingQualityScore',
    displayName: 'Writing Quality Score',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunction: 'avg',
    canBeExported: true
  },
  imageScore: {
    name: 'imageScore',
    displayName: 'Image Score',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunction: 'avg',
    canBeExported: true
  },
  videoScore: {
    name: 'videoScore',
    displayName: 'Video Score',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunction: 'avg',
    canBeExported: true
  },
  childWeeklyRatingVolume: {
    name: RatingsAndReviewMetric.WEEKLY_RATING_VOLUME,
    displayName: 'Weekly Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    canBeExported: true
  },
  childPositiveWeeklyRatingVolume: {
    name: RatingsAndReviewMetric.CHILD_POSITIVE_WEEKLY_RATING_VOLUME,
    displayName: 'Weekly Positive Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    canBeExported: true
  },
  // ! This is a computed field
  weeklyRating: {
    name: 'weeklyRating',
    displayName: 'Weekly Rating',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunctionType: 'computed',
    aggregationFunction: expressionParser.parse(
      '(childPositiveWeeklyRatingVolume > 0 ? weightedChildWeeklyRating / childPositiveWeeklyRatingVolume : 0)'
    )
  },

  parentWeeklyRating: {
    name: 'parentWeeklyRating',
    displayName: 'Weekly Rating',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunctionType: 'computed',
    aggregationFunction: expressionParser.parse(
      '(parentPositiveWeeklyRatingVolume > 0 ? weightedParentWeeklyRating / parentPositiveWeeklyRatingVolume : 0)'
    )
  },

  childLifetimeRatingVolume: {
    name: 'childLifetimeRatingVolume',
    displayName: 'Cumulative Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    canBeExported: true,
    timePeriodAggregationFunctionType: 'lastValue'
  },
  parentLifetimeRating: {
    name: 'lifetimeRating',
    displayName: 'Cumulative Rating',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunctionType: 'computed',
    aggregationFunction: expressionParser.parse(
      '(parentLifetimeRatingVolume > 0 ? weightedParentLifetimeRating / parentLifetimeRatingVolume : 0)'
    ),
    canBeExported: true
  },
  lifetimeRating: {
    name: 'lifetimeRating',
    displayName: 'Cumulative Rating',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunctionType: 'computed',
    aggregationFunction: expressionParser.parse(
      '(childLifetimeRatingVolume > 0 ? weightedChildLifetimeRating / childLifetimeRatingVolume : 0)'
    ),
    canBeExported: true
  },
  childWeeklyOneStarRatingCount: {
    name: 'childWeeklyOneStarRatingCount',
    displayName: 'Weekly One Star Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  childWeeklyTwoStarsRatingCount: {
    name: 'childWeeklyTwoStarsRatingCount',
    displayName: 'Weekly Two Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  childWeeklyThreeStarsRatingCount: {
    name: 'childWeeklyThreeStarsRatingCount',
    displayName: 'Weekly Three Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  childWeeklyFourStarsRatingCount: {
    name: 'childWeeklyFourStarsRatingCount',
    displayName: 'Weekly Four Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  childWeeklyFiveStarsRatingCount: {
    name: 'childWeeklyFiveStarsRatingCount',
    displayName: 'Weekly Five Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  weeklyOneStarReviewVolume: {
    name: 'weeklyOneStarReviewVolume',
    displayName: 'Weekly One Star Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  weeklyTwoStarsReviewVolume: {
    name: 'weeklyTwoStarsReviewVolume',
    displayName: 'Weekly Two Stars Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  weeklyThreeStarsReviewVolume: {
    name: 'weeklyThreeStarsReviewVolume',
    displayName: 'Weekly Three Stars Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  weeklyFourStarsReviewVolume: {
    name: 'weeklyFourStarsReviewVolume',
    displayName: 'Weekly Four Stars Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  weeklyFiveStarsReviewVolume: {
    name: 'weeklyFiveStarsReviewVolume',
    displayName: 'Weekly Five Stars Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  weeklyReviewVolume: {
    name: 'weeklyReviewVolume',
    displayName: 'Weekly Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  lifetimeReviewVolume: {
    name: 'lifetimeReviewVolume',
    displayName: 'Cumulative Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum'
  },
  weightedLifetimeReviewRating: {
    name: 'weightedLifetimeReviewRating',
    displayName: 'Weighted Cumulative Review Rating',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunction: 'sum'
  },
  // ! computed value
  lifetimeReviewRating: {
    name: 'lifetimeReviewRating',
    displayName: 'Cumulative Review Rating',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunctionType: 'computed',
    aggregationFunction: expressionParser.parse(
      '(lifetimeReviewVolume > 0 ? weightedLifetimeReviewRating / lifetimeReviewVolume : 0)'
    )
  },
  weightedWeeklyReviewRating: {
    name: 'weightedWeeklyReviewRating',
    displayName: 'Weighted Weekly Review Rating',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunction: 'sum'
  },

  // ! computed value
  weeklyReviewRating: {
    name: 'weeklyReviewRating',
    displayName: 'Weekly Review Rating',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunctionType: 'computed',
    aggregationFunction: expressionParser.parse(
      '(weeklyReviewVolume > 0 ? weightedWeeklyReviewRating / weeklyReviewVolume : 0)'
    )
  },

  /**
   * Sentiment Fields
   */

  weeklyPositiveSentimentReviewVolume: {
    name: 'weeklyPositiveSentimentReviewVolume',
    displayName: 'Weekly Positive Sentiment Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum'
  },

  weeklyPosSentimentValue: {
    name: 'weeklyPosSentimentValue',
    displayName: 'Weekly Positive Sentiment Value',
    metricType: METRICTYPE.PERCENT,
    aggregationFunctionType: 'computed',
    aggregationFunction: expressionParser.parse(
      '(weeklyPositiveSentimentReviewVolume > 0 ? weeklyPositiveSentimentReviewVolume / (weeklyPositiveSentimentReviewVolume + weeklyNeutralSentimentReviewVolume + weeklyNegativeSentimentReviewVolume) : 0)'
    )
  },

  weeklyNeutralSentimentReviewVolume: {
    name: 'weeklyNeutralSentimentReviewVolume',
    displayName: 'Weekly Neutral Sentiment Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum'
  },

  weeklyNeuSentimentValue: {
    name: 'weeklyNeuSentimentValue',
    displayName: 'Weekly Neutral Sentiment Value',
    metricType: METRICTYPE.PERCENT,
    aggregationFunctionType: 'computed',
    aggregationFunction: expressionParser.parse(
      '(weeklyNeutralSentimentReviewVolume > 0 ? weeklyNeutralSentimentReviewVolume / (weeklyPositiveSentimentReviewVolume + weeklyNeutralSentimentReviewVolume + weeklyNegativeSentimentReviewVolume) : 0)'
    )
  },

  weeklyNegativeSentimentReviewVolume: {
    name: 'weeklyNegativeSentimentReviewVolume',
    displayName: 'Weekly Negative Sentiment Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum'
  },

  weeklyNegSentimentValue: {
    name: 'weeklyNegSentimentValue',
    displayName: 'Weekly Negative Sentiment Value',
    metricType: METRICTYPE.PERCENT,
    aggregationFunctionType: 'computed',
    aggregationFunction: expressionParser.parse(
      '(weeklyNegativeSentimentReviewVolume > 0 ? weeklyNegativeSentimentReviewVolume / (weeklyPositiveSentimentReviewVolume + weeklyNeutralSentimentReviewVolume + weeklyNegativeSentimentReviewVolume) : 0)'
    )
  },
  aspectSentiment: {
    name: 'aspectSentiment',
    displayName: 'Aspect Sentiment',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'value_count'
  },

  reviewSentiment: {
    name: 'reviewSentiment',
    displayName: 'Review Sentiment',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'value_count'
  },

  lifetimePositiveSentimentReviewVolume: {
    name: 'lifetimePositiveSentimentReviewVolume',
    displayName: 'Lifetime Positive Sentiment Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum'
  },

  lifetimeNeutralSentimentReviewVolume: {
    name: 'lifetimeNeutralSentimentReviewVolume',
    displayName: 'Lifetime Neutral Sentiment Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum'
  },

  lifetimeNegativeSentimentReviewVolume: {
    name: 'lifetimeNegativeSentimentReviewVolume',
    displayName: 'Lifetime Negative Sentiment Review Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum'
  },

  childLifetimeOneStarRatingCount: {
    name: 'childLifetimeOneStarRatingCount',
    displayName: 'Cumulative One Star Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  childLifetimeTwoStarsRatingCount: {
    name: 'childLifetimeTwoStarsRatingCount',
    displayName: 'Cumulative Two Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  childLifetimeThreeStarsRatingCount: {
    name: 'childLifetimeThreeStarsRatingCount',
    displayName: 'Cumulative Three Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  childLifetimeFourStarsRatingCount: {
    name: 'childLifetimeFourStarsRatingCount',
    displayName: 'Cumulative Four Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  childLifetimeFiveStarsRatingCount: {
    name: 'childLifetimeFiveStarsRatingCount',
    displayName: 'Cumulative Five Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },
  // Parent Weekly Rating Counts - Parent Level NOT divided by totalSiblings, should be used for sku level view
  parentWeeklyOneStarRatingCount: {
    name: 'parentWeeklyOneStarRatingCount',
    displayName: 'Weekly One Star Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },

  parentWeeklyTwoStarsRatingCount: {
    name: 'parentWeeklyTwoStarsRatingCount',
    displayName: 'Weekly Two Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },

  parentWeeklyThreeStarsRatingCount: {
    name: 'parentWeeklyThreeStarsRatingCount',
    displayName: 'Weekly Three Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },

  parentWeeklyFourStarsRatingCount: {
    name: 'parentWeeklyFourStarsRatingCount',
    displayName: 'Weekly Four Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },

  parentWeeklyFiveStarsRatingCount: {
    name: 'parentWeeklyFiveStarsRatingCount',
    displayName: 'Weekly Five Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },

  parentWeeklyRatingVolume: {
    name: RatingsAndReviewMetric.PARENT_WEEKLY_RATING_VOLUME,
    displayName: 'Weekly Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    canBeExported: true
  },
  parentPositiveWeeklyRatingVolume: {
    name: RatingsAndReviewMetric.PARENT_POSITIVE_WEEKLY_RATING_VOLUME,
    displayName: 'Weekly Positive Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    canBeExported: true
  },
  // Parent Lifetime Rating Counts
  parentLifetimeOneStarRatingCount: {
    name: 'parentLifetimeOneStarRatingCount',
    displayName: 'Cumulative One Star Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },

  parentLifetimeTwoStarsRatingCount: {
    name: 'parentLifetimeTwoStarsRatingCount',
    displayName: 'Cumulative Two Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },

  parentLifetimeThreeStarsRatingCount: {
    name: 'parentLifetimeThreeStarsRatingCount',
    displayName: 'Cumulative Three Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },

  parentLifetimeFourStarsRatingCount: {
    name: 'parentLifetimeFourStarsRatingCount',
    displayName: 'Cumulative Four Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },

  parentLifetimeFiveStarsRatingCount: {
    name: 'parentLifetimeFiveStarsRatingCount',
    displayName: 'Cumulative Five Stars Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    aggregationFunctionType: 'sum'
  },

  parentLifetimeRatingVolume: {
    name: 'parentLifetimeRatingVolume',
    displayName: 'Cumulative Rating Volume',
    metricType: METRICTYPE.VOLUME,
    aggregationFunction: 'sum',
    canBeExported: true
  },

  // New Weighted Fields
  weightedChildLifetimeRating: {
    name: 'weightedChildLifetimeRating',
    displayName: 'Weighted Cumulative Rating',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunction: 'sum',
    canBeExported: true
  },

  weightedParentLifetimeRating: {
    name: 'weightedParentLifetimeRating',
    displayName: 'Weighted Cumulative Rating',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunction: 'sum',
    canBeExported: true
  },

  weightedChildWeeklyRating: {
    name: 'weightedChildWeeklyRating',
    displayName: 'Weighted Weekly Rating',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunction: 'sum',
    canBeExported: true
  },

  weightedParentWeeklyRating: {
    name: 'weightedParentWeeklyRating',
    displayName: 'Weighted Parent Weekly Rating',
    metricType: METRICTYPE.DECIMAL,
    aggregationFunction: 'sum',
    canBeExported: true
  }
};
